/* ENRAGED — product page: gallery, variant picker, product form, sticky add to cart, recommendations.
   Components are custom elements, so section reloads in the theme editor reconnect cleanly. */
(() => {
  if (window.__enragedProduct) return;
  window.__enragedProduct = true;

  const theme = window.theme || {};
  const routes = theme.routes || {};
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const money = (cents) => theme.formatMoney(cents, theme.moneyFormat);
  const moneyShort = (cents) => money(cents).replace(/[.,]00(?!\d)/, '');

  const parseMoney = (text) => {
    const s = (text || '').replace(/[^\d.,]/g, '');
    if (!s) return NaN;
    const lastComma = s.lastIndexOf(',');
    const lastDot = s.lastIndexOf('.');
    const normalized = lastComma > lastDot && s.length - lastComma - 1 === 2
      ? s.replace(/\./g, '').replace(',', '.')
      : s.replace(/,/g, '');
    return Math.round(parseFloat(normalized) * 100);
  };

  /* ---------- Section controller ---------- */
  class ProductSection extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      const json = this.querySelector('[data-product-json]');
      this.product = json ? JSON.parse(json.textContent) : null;
      this.form = document.getElementById('ProductForm-' + this.sectionId);
      const idInput = this.form && this.form.querySelector('[name="id"]');
      this.variant = this.product && idInput ? this.product.variants.find((v) => String(v.id) === idInput.value) || null : null;

      this.onVariantChange = (event) => this.applyVariant(event.detail.variant);
      this.onChange = (event) => {
        if (event.target.name === 'selling_plan') this.renderPrice();
      };
      this.onClick = (event) => {
        const step = event.target.closest('.product__quantity [data-qty-step]');
        if (!step) return;
        const input = step.closest('.qty').querySelector('input');
        const min = Number(input.min) || 1;
        const max = input.max ? Number(input.max) : Infinity;
        const next = Math.min(max, Math.max(min, (parseInt(input.value, 10) || min) + Number(step.dataset.qtyStep)));
        input.value = next;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      };
      this.addEventListener('variant:change', this.onVariantChange);
      this.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onClick);
      this.observeBundlerPrice();
    }

    disconnectedCallback() {
      this.removeEventListener('variant:change', this.onVariantChange);
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('click', this.onClick);
      if (this.priceObserver) this.priceObserver.disconnect();
    }

    applyVariant(variant) {
      this.variant = variant;
      this.updateUrl(variant);
      this.updateInputs(variant);
      this.renderPrice();
      this.updateButtons(variant);
      this.updateOptionAvailability();
      if (variant && variant.featured_media) this.querySelector('product-gallery')?.showMedia(variant.featured_media.id);
      const sku = this.querySelector('[data-sku]');
      if (sku) {
        sku.hidden = !(variant && variant.sku);
        const value = sku.querySelector('[data-sku-value]');
        if (value) value.textContent = (variant && variant.sku) || '';
      }
      this.querySelectorAll('[data-selected-value]').forEach((el, i) => {
        if (variant) el.textContent = variant.options[i];
      });
    }

    /* Keep every existing query parameter (UTMs, fbclid, etc.) and only swap ?variant= */
    updateUrl(variant) {
      if (!variant) return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState(window.history.state, '', url.toString());
    }

    updateInputs(variant) {
      document.querySelectorAll(`#ProductForm-${this.sectionId} [name="id"], #ProductInstallments-${this.sectionId} [name="id"]`).forEach((input) => {
        input.value = variant ? variant.id : '';
        input.disabled = !variant;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    selectedPlanId() {
      const checked = this.form && [...this.form.elements].find((el) => el.name === 'selling_plan' && el.checked);
      return checked && checked.value ? Number(checked.value) : null;
    }

    renderPrice() {
      const variant = this.variant;
      if (!variant) return;
      let price = variant.price;
      let compare = variant.compare_at_price || 0;
      const planId = this.selectedPlanId();
      if (planId) {
        const allocation = (variant.selling_plan_allocations || []).find((a) => a.selling_plan_id === planId);
        if (allocation) {
          price = allocation.price;
          compare = allocation.compare_at_price > allocation.price ? allocation.compare_at_price : variant.price;
        }
      }
      this.updatingPrice = true;
      document.querySelectorAll(`product-section[data-section="${this.sectionId}"] [data-price-wrapper]`).forEach((wrapper) => {
        const onSale = compare > price;
        const priceEl = wrapper.querySelector('[data-price]');
        const compareEl = wrapper.querySelector('[data-compare-price]');
        if (priceEl) priceEl.textContent = money(price);
        if (compareEl) {
          compareEl.textContent = onSale ? money(compare) : '';
          compareEl.hidden = !onSale;
        }
        wrapper.querySelectorAll('[data-sale-label], [data-compare-label]').forEach((el) => { el.hidden = !onSale; });
        wrapper.classList.toggle('e-price--sale', onSale);
        this.setBadge(wrapper, onSale ? compare - price : 0);
      });
      this.updatingPrice = false;
    }

    setBadge(wrapper, savingCents) {
      const badge = wrapper.querySelector('[data-save-badge]');
      if (!badge) return;
      const show = savingCents > 0;
      if (badge.hidden === !show && !show) return;
      const label = show ? (theme.strings.saveAmount || 'Save [amount]').replace('[amount]', moneyShort(savingCents)) : '';
      if (badge.hidden === !show && badge.textContent === label) return;
      badge.hidden = !show;
      if (show) badge.textContent = label;
    }

    /* The Atlas bundler rewrites .main-price / .compare-price when a tier is chosen.
       Watch only those nodes. Watching the whole price block retriggers this callback
       when the savings badge updates, which freezes the tab. */
    observeBundlerPrice() {
      const main = this.querySelector('.main-price');
      if (!main || !('MutationObserver' in window)) return;
      const wrapper = main.closest('[data-price-wrapper]');
      const compareEl = wrapper && wrapper.querySelector('.compare-price');
      const sync = () => {
        if (this.updatingPrice) return;
        const price = parseMoney(main.textContent);
        const compareVisible = compareEl && !compareEl.hidden && compareEl.offsetParent !== null;
        const compare = compareVisible ? parseMoney(compareEl.textContent) : NaN;
        const onSale = compare > price;
        this.setBadge(wrapper, onSale ? compare - price : 0);
        const sticky = document.querySelector(`sticky-atc[data-section="${this.sectionId}"]`);
        if (!sticky || Number.isNaN(price)) return;
        const stickyPrice = sticky.querySelector('[data-price]');
        const stickyCompare = sticky.querySelector('[data-compare-price]');
        const nextPrice = main.textContent.trim();
        if (stickyPrice && stickyPrice.textContent !== nextPrice) stickyPrice.textContent = nextPrice;
        if (stickyCompare) {
          stickyCompare.hidden = !onSale;
          if (onSale) stickyCompare.textContent = compareEl.textContent.trim();
        }
      };
      this.priceObserver = new MutationObserver(sync);
      const options = { childList: true, characterData: true, subtree: true };
      this.priceObserver.observe(main, options);
      if (compareEl) this.priceObserver.observe(compareEl, options);
    }

    updateButtons(variant) {
      const s = theme.strings || {};
      let label = s.addToCart;
      let disabled = false;
      if (!variant) { label = s.unavailable; disabled = true; }
      else if (!variant.available) { label = s.soldOut; disabled = true; }
      const button = this.form && this.form.querySelector('[name="add"]');
      if (button) {
        button.disabled = disabled;
        const span = button.querySelector('[data-add-label]');
        if (span) span.textContent = label;
      }
      const sticky = document.querySelector(`sticky-atc[data-section="${this.sectionId}"]`);
      if (sticky) {
        const stickyButton = sticky.querySelector('[data-sticky-submit]');
        stickyButton.disabled = disabled;
        const span = stickyButton.querySelector('[data-sticky-label]');
        if (span) span.textContent = label;
      }
    }

    updateOptionAvailability() {
      const picker = this.querySelector('variant-picker');
      if (!picker || !this.product) return;
      const selected = picker.selectedValues();
      picker.querySelectorAll('fieldset[data-option-index]').forEach((fieldset) => {
        const index = Number(fieldset.dataset.optionIndex);
        fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
          const candidate = selected.slice();
          candidate[index] = input.value;
          const available = this.product.variants.some((v) => v.available && v.options.every((o, i) => o === candidate[i]));
          const label = fieldset.querySelector(`label[for="${input.id}"]`);
          if (label) label.classList.toggle('is-unavailable', !available);
        });
      });
    }
  }
  if (!customElements.get('product-section')) customElements.define('product-section', ProductSection);

  /* ---------- Variant picker ---------- */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.onChange = () => {
        const section = this.closest('product-section');
        if (!section || !section.product) return;
        const values = this.selectedValues();
        const variant = section.product.variants.find((v) => v.options.every((o, i) => o === values[i])) || null;
        this.dispatchEvent(new CustomEvent('variant:change', { detail: { variant }, bubbles: true }));
      };
      this.addEventListener('change', this.onChange);
    }
    disconnectedCallback() { this.removeEventListener('change', this.onChange); }
    selectedValues() {
      return [...this.querySelectorAll('[data-option-index]')]
        .sort((a, b) => Number(a.dataset.optionIndex) - Number(b.dataset.optionIndex))
        .map((el) => (el.tagName === 'SELECT' ? el.value : (el.querySelector('input:checked') || {}).value));
    }
  }
  if (!customElements.get('variant-picker')) customElements.define('variant-picker', VariantPicker);

  /* ---------- Product form (theme add to cart). Atlas bundler handles its own submit first. ---------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.error = this.querySelector('[data-form-error]');
      this.onSubmit = this.onSubmit.bind(this);
      this.form.addEventListener('submit', this.onSubmit);
    }
    disconnectedCallback() { if (this.form) this.form.removeEventListener('submit', this.onSubmit); }

    async onSubmit(event) {
      if (event.defaultPrevented) return;
      const drawer = document.querySelector('cart-drawer');
      if (theme.cartType !== 'drawer' || !drawer) return;
      event.preventDefault();
      const button = this.form.querySelector('[name="add"]');
      if (!button || button.disabled || button.getAttribute('aria-busy') === 'true') return;
      if (this.error) this.error.hidden = true;
      button.classList.add('is-loading');
      button.setAttribute('aria-busy', 'true');
      const body = new FormData(this.form);
      body.append('sections', drawer.getSectionsToRender().map((s) => s.section).join(','));
      body.append('sections_url', window.location.pathname);
      try {
        const response = await fetch(routes.cartAdd + '.js', {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body,
        });
        const data = await response.json();
        if (!response.ok || data.status) throw new Error(data.description || data.message || theme.strings.cartError);
        drawer.setActiveElement(button);
        await drawer.renderContents(data);
        theme.announce(theme.strings.added);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'product-form', __enraged: true } }));
      } catch (error) {
        if (this.error) {
          this.error.textContent = error.message;
          this.error.hidden = false;
        }
        theme.announce(error.message);
      } finally {
        button.classList.remove('is-loading');
        button.removeAttribute('aria-busy');
      }
    }
  }
  if (!customElements.get('product-form')) customElements.define('product-form', ProductForm);

  /* ---------- Gallery ---------- */
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gallery-track]');
      if (!this.track) return;
      this.slides = [...this.track.querySelectorAll('.gallery__slide')];
      this.thumbs = [...this.querySelectorAll('[data-thumb]')];
      this.current = this.querySelector('[data-gallery-current]');
      this.prev = this.querySelector('[data-gallery-prev]');
      this.next = this.querySelector('[data-gallery-next]');
      this.index = 0;
      this.onScroll = theme.debounce(() => this.sync(), 60);
      this.onClick = (event) => {
        const thumb = event.target.closest('[data-thumb]');
        if (thumb) return this.go(Number(thumb.dataset.thumb));
        if (event.target.closest('[data-gallery-prev]')) return this.go(this.index - 1);
        if (event.target.closest('[data-gallery-next]')) return this.go(this.index + 1);
        return undefined;
      };
      this.onKeydown = (event) => {
        if (event.key === 'ArrowRight') { event.preventDefault(); this.go(this.index + 1); }
        if (event.key === 'ArrowLeft') { event.preventDefault(); this.go(this.index - 1); }
      };
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.track.addEventListener('keydown', this.onKeydown);
      this.addEventListener('click', this.onClick);
      this.setActive(0);
    }
    disconnectedCallback() {
      if (!this.track) return;
      this.track.removeEventListener('scroll', this.onScroll);
      this.track.removeEventListener('keydown', this.onKeydown);
      this.removeEventListener('click', this.onClick);
    }
    go(i, instant) {
      const index = Math.max(0, Math.min(this.slides.length - 1, i));
      const slide = this.slides[index];
      if (!slide) return;
      this.track.scrollTo({ left: slide.offsetLeft, behavior: instant || reducedMotion.matches ? 'auto' : 'smooth' });
      this.setActive(index);
    }
    sync() {
      const width = this.track.clientWidth || 1;
      this.setActive(Math.round(this.track.scrollLeft / width));
    }
    setActive(index) {
      this.index = index;
      this.thumbs.forEach((thumb, i) => {
        if (i === index) {
          thumb.setAttribute('aria-current', 'true');
          const strip = thumb.closest('.gallery__thumbs');
          if (strip && strip.scrollWidth > strip.clientWidth) {
            const li = thumb.parentElement;
            strip.scrollTo({ left: li.offsetLeft - strip.clientWidth / 2 + li.clientWidth / 2, behavior: 'auto' });
          }
        } else {
          thumb.removeAttribute('aria-current');
        }
      });
      if (this.current) this.current.textContent = String(index + 1);
      if (this.prev) this.prev.disabled = index <= 0;
      if (this.next) this.next.disabled = index >= this.slides.length - 1;
      this.slides.forEach((slide, i) => {
        if (i !== index) slide.querySelectorAll('e-video').forEach((v) => v.video && !v.video.paused && v.pause(false));
      });
    }
    showMedia(mediaId) {
      const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      if (index > -1) this.go(index, true);
    }
  }
  if (!customElements.get('product-gallery')) customElements.define('product-gallery', ProductGallery);

  /* ---------- Sticky add to cart (mobile) ---------- */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      this.form = document.getElementById('ProductForm-' + this.dataset.section);
      this.mainButton = this.form && this.form.querySelector('[name="add"]');
      this.button = this.querySelector('[data-sticky-submit]');
      if (!this.mainButton || !this.button || !('IntersectionObserver' in window)) return;
      this.hidden = false;
      this.setVisible(false);
      this.onClick = () => {
        if (this.mainButton.disabled) return;
        this.mainButton.click();
      };
      this.button.addEventListener('click', this.onClick);
      this.pastButton = false;
      this.footerVisible = false;
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.target === this.mainButton) this.pastButton = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          else this.footerVisible = entry.isIntersecting;
        });
        this.setVisible(this.pastButton && !this.footerVisible);
      });
      this.observer.observe(this.mainButton);
      const footer = document.querySelector('.footer');
      if (footer) this.observer.observe(footer);
    }
    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.button && this.onClick) this.button.removeEventListener('click', this.onClick);
      document.body.classList.remove('has-sticky-atc');
    }
    setVisible(show) {
      this.classList.toggle('is-visible', show);
      this.setAttribute('aria-hidden', String(!show));
      if (this.button) this.button.tabIndex = show ? 0 : -1;
      document.body.classList.toggle('has-sticky-atc', show);
    }
  }
  if (!customElements.get('sticky-atc')) customElements.define('sticky-atc', StickyAtc);

  /* ---------- Recommendations (lazy) ---------- */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.loaded || !this.dataset.url || this.children.length) return;
      this.observer = new IntersectionObserver((entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        this.observer.disconnect();
        this.load();
      }, { rootMargin: '600px 0px' });
      this.observer.observe(this);
    }
    disconnectedCallback() { if (this.observer) this.observer.disconnect(); }
    async load() {
      this.loaded = true;
      try {
        const html = await fetch(this.dataset.url).then((r) => r.text());
        const fresh = theme.parseHTML(html).querySelector('product-recommendations');
        if (fresh && fresh.innerHTML.trim()) {
          this.innerHTML = fresh.innerHTML;
          this.querySelectorAll('.reveal').forEach((el) => el.classList.add('is-visible'));
        } else {
          const section = this.closest('.shopify-section');
          if (section) section.hidden = true;
        }
      } catch (error) {
        /* recommendations are optional */
      }
    }
  }
  if (!customElements.get('product-recommendations')) customElements.define('product-recommendations', ProductRecommendations);
})();
